/**
 * Regulated types and their hazards (SPEC_Hazard_UI §2.1).
 *
 * This is a worklist, not a catalogue: the default order puts open releases
 * first and then sorts by highest severity descending. Alphabetical would look
 * tidier and would bury the work.
 */

import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";

import { useModalContext } from "../../components/ModalProvider";
import { useLegalAdministrationAPI } from "../../LegalAdministrationAPI";
import {
  DAMAGE_TYPES,
  HazardChip,
  HazardChipLegend,
  SeveritySummary,
  SignoffPill,
  damageTypeLabel,
  daysUntil,
  formatDate,
} from "./hazardDisplay";
import HazardTypeDetailPanel from "./HazardTypeDetailPanel";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface Properties {
  refreshSignal: number;
  openCode?: string | null;
  onOpenCode: (code: string | null) => void;
  onStartReview: (code: string) => void;
  onChanged: () => void;
}

/** Release states first, in the order the work is picked up. */
const SIGNOFF_ORDER: Record<string, number> = { never: 0, stale: 1, expired: 2, released: 3 };

/** Fewer than this many days to recertification gets a note in the row (§2.1). */
const RECERT_WARNING_DAYS = 90;

export default function HazardTypesPanel(properties: Properties) {
  const { t } = useTranslation();
  const { pushError } = useModalContext() as any;
  const legalAPI = useLegalAdministrationAPI();
  const fieldId = useId();

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [signoffFilter, setSignoffFilter] = useState<string>("all");
  const [damageTypeFilter, setDamageTypeFilter] = useState<string>("all");
  const [specialFilter, setSpecialFilter] = useState<string>("all");

  const load = async () => {
    try {
      setLoading(true);
      const result = await legalAPI.getHazardTypes();
      setRows(result?.rows || []);
    } catch (error) {
      pushError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties.refreshSignal]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return rows
      .filter((row: any) => {
        if (needle && !`${row.name} ${row.regulatedObjectCode}`.toLowerCase().includes(needle)) {
          return false;
        }
        if (signoffFilter !== "all" && row.signoffState !== signoffFilter) return false;
        if (
          damageTypeFilter !== "all" &&
          !(row.hazards || []).some((hazard: any) => hazard.damageType === damageTypeFilter)
        ) {
          return false;
        }
        if (specialFilter === "hasOverride" && !row.hasOverride) return false;
        if (specialFilter === "noAssignment" && row.assignmentCount > 0) return false;
        return true;
      })
      .sort((a: any, b: any) => {
        const orderA = SIGNOFF_ORDER[a.signoffState] ?? 9;
        const orderB = SIGNOFF_ORDER[b.signoffState] ?? 9;
        if (orderA !== orderB) return orderA - orderB;
        // Not-yet-derivable severity sorts last within its group rather than as
        // zero — it is an open task, not a low-severity one.
        const siA = a.highestSiBase;
        const siB = b.highestSiBase;
        if (siA === null && siB === null) return a.name.localeCompare(b.name);
        if (siA === null) return 1;
        if (siB === null) return -1;
        return siB - siA;
      });
  }, [rows, search, signoffFilter, damageTypeFilter, specialFilter]);

  if (properties.openCode) {
    return (
      <HazardTypeDetailPanel
        regulatedObjectCode={properties.openCode}
        onStartReview={properties.onStartReview}
        onClose={(didChange: boolean) => {
          properties.onOpenCode(null);
          if (didChange) {
            load();
            properties.onChanged();
          }
        }}
      />
    );
  }

  const searchInputId = `${fieldId}-search`;
  const signoffSelectId = `${fieldId}-signoff`;
  const damageTypeSelectId = `${fieldId}-damage-type`;
  const specialSelectId = `${fieldId}-special`;
  const filtersActive =
    !!search.trim() ||
    signoffFilter !== "all" ||
    damageTypeFilter !== "all" ||
    specialFilter !== "all";

  const resetFilters = () => {
    setSearch("");
    setSignoffFilter("all");
    setDamageTypeFilter("all");
    setSpecialFilter("all");
  };

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
      <div className="flex min-w-0 flex-wrap items-end gap-4">
        <div className="flex w-[280px] max-w-full flex-col gap-1.5">
          <Label htmlFor={searchInputId}>{t("hazard.types.searchLabel")}</Label>
          <div className="relative">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              id={searchInputId}
              type="search"
              className="pl-9"
              placeholder={t("hazard.types.searchPlaceholder")}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>

        <div className="flex w-[240px] max-w-full flex-col gap-1.5">
          <Label htmlFor={signoffSelectId}>{t("hazard.types.release")}</Label>
          <Select value={signoffFilter} onValueChange={setSignoffFilter}>
            <SelectTrigger id={signoffSelectId}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-w-[calc(100vw-2rem)]">
              <SelectItem value="all">{t("hazard.filter.allReleaseStates")}</SelectItem>
              <SelectItem value="never">{t("hazard.signoff.never")}</SelectItem>
              <SelectItem value="stale">{t("hazard.signoff.stale")}</SelectItem>
              <SelectItem value="expired">{t("hazard.signoff.expired")}</SelectItem>
              <SelectItem value="released">{t("hazard.signoff.released")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex w-[220px] max-w-full flex-col gap-1.5">
          <Label htmlFor={damageTypeSelectId}>{t("hazard.types.damageType")}</Label>
          <Select value={damageTypeFilter} onValueChange={setDamageTypeFilter}>
            <SelectTrigger id={damageTypeSelectId}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-w-[calc(100vw-2rem)]">
              <SelectItem value="all">{t("hazard.filter.allDamageTypes")}</SelectItem>
              {DAMAGE_TYPES.map((damageType) => (
                <SelectItem key={damageType} value={damageType}>
                  {damageTypeLabel(t, damageType)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex w-[240px] max-w-full flex-col gap-1.5">
          <Label htmlFor={specialSelectId}>{t("hazard.filter.specialLabel")}</Label>
          <Select value={specialFilter} onValueChange={setSpecialFilter}>
            <SelectTrigger id={specialSelectId}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-w-[calc(100vw-2rem)]">
              <SelectItem value="all">{t("hazard.filter.noSpecial")}</SelectItem>
              <SelectItem value="hasOverride">{t("hazard.filter.hasOverride")}</SelectItem>
              <SelectItem value="noAssignment">{t("hazard.filter.noAssignment")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <p className="text-sm text-muted-foreground max-sm:w-full sm:pb-2.5">
          {t("hazard.types.countShown", { shown: filtered.length, total: rows.length })}
        </p>
      </div>

      <HazardChipLegend t={t} />

      {loading ? (
        <div className="flex min-w-0 flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-[260px] w-full" />
        </div>
      ) : (
        <div className="min-w-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[230px]">{t("hazard.types.type")}</TableHead>
                <TableHead>{t("hazard.types.hazards")}</TableHead>
                <TableHead className="w-[190px]">{t("hazard.types.severity")}</TableHead>
                <TableHead className="w-[180px]">{t("hazard.types.release")}</TableHead>
                <TableHead className="w-[170px]">{t("hazard.types.lastFullReview")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((row: any) => {
                const recertDays = daysUntil(row.recertDueAt);
                return (
                  <TableRow key={row.regulatedObjectCode}>
                    <TableCell>
                      <button
                        type="button"
                        className="text-left font-semibold text-primary underline underline-offset-2 hover:no-underline"
                        onClick={() => properties.onOpenCode(row.regulatedObjectCode)}
                      >
                        {row.name}
                      </button>
                      <div className="text-xs text-muted-foreground">
                        {row.din276?.length ? row.din276.join(", ") : row.regulatedObjectCode}
                      </div>
                    </TableCell>
                    <TableCell>
                      {row.assignmentCount === 0 ? (
                        <span className="text-sm text-destructive">
                          {t("hazard.types.noAssignmentsShort")}
                        </span>
                      ) : (
                        (row.hazards || []).map((hazard: any) => (
                          <HazardChip
                            key={hazard.hazardCode}
                            label={hazard.label}
                            damageType={hazard.damageType}
                            relation={hazard.relation}
                            t={t}
                          />
                        ))
                      )}
                    </TableCell>
                    <TableCell>
                      <SeveritySummary
                        summary={row.severitySummary}
                        t={t}
                        onClick={() => properties.onOpenCode(row.regulatedObjectCode)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-2">
                        <SignoffPill state={row.signoffState} t={t} />
                        {row.hasOverride ? (
                          <span className="text-xs text-warning">
                            {t("hazard.types.hasOverride")}
                          </span>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatDate(row.lastFullReviewAt)}
                      {recertDays !== null && recertDays < RECERT_WARNING_DAYS ? (
                        <div
                          className={
                            recertDays < 0 ? "text-xs text-destructive" : "text-xs text-warning"
                          }
                        >
                          {recertDays < 0
                            ? t("hazard.types.recertOverdue")
                            : t("hazard.types.recertSoon", { days: recertDays })}
                        </div>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {!loading && filtered.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <p className="text-sm font-semibold">
            {rows.length === 0 ? t("hazard.types.emptyTitle") : t("hazard.types.noMatches")}
          </p>
          <p className="max-w-prose text-sm text-muted-foreground">
            {rows.length === 0 ? t("hazard.types.emptyHint") : t("hazard.types.noMatchesHint")}
          </p>
          {filtersActive ? (
            <Button variant="outline" size="sm" onClick={resetFilters}>
              {t("hazard.types.resetFilters")}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
